import { PointerEvent as ReactPointerEvent, ReactNode, useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

type DraggableModalPanelProps = {
  open: boolean;
  title: string;
  subtitle?: string;
  onClose: () => void;
  children: ReactNode;
  /** Rodapé fixo dentro do card (botões de ação). */
  footer?: ReactNode;
  /** Classe do card (largura máxima etc.). */
  className?: string;
  /** Classe da área rolável do conteúdo. */
  bodyClassName?: string;
  /** Classe do overlay (ex.: z-[70] para empilhar sobre outro modal). */
  overlayClassName?: string;
  closeDisabled?: boolean;
};

type DragState = {
  pointerId: number;
  startX: number;
  startY: number;
  originX: number;
  originY: number;
};

const modalEscapeClosers: Array<() => void> = [];

/**
 * Overlay centralizado com painel arrastável pela barra de título.
 * Renderizado em portal no document.body para cobrir header e sidebar.
 */
export function DraggableModalPanel({
  open,
  title,
  subtitle,
  onClose,
  children,
  footer,
  className = 'w-full max-w-lg',
  bodyClassName = 'erp-card-body min-h-0 flex-1 overflow-y-auto',
  overlayClassName = 'z-50',
  closeDisabled = false,
}: DraggableModalPanelProps) {
  const titleId = useId();
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const dragRef = useRef<DragState | null>(null);

  useEffect(() => {
    if (!open) return;
    setOffset({ x: 0, y: 0 });
    dragRef.current = null;
  }, [open]);

  useEffect(() => {
    if (!open) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const close = (): void => {
      if (!closeDisabled) onClose();
    };
    modalEscapeClosers.push(close);

    function handleKeyDown(event: KeyboardEvent): void {
      if (event.key !== 'Escape') return;
      if (modalEscapeClosers[modalEscapeClosers.length - 1] !== close) return;
      event.preventDefault();
      close();
    }

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      const index = modalEscapeClosers.lastIndexOf(close);
      if (index >= 0) modalEscapeClosers.splice(index, 1);
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, closeDisabled, onClose]);

  if (!open) return null;

  function handlePointerDown(event: ReactPointerEvent<HTMLDivElement>): void {
    if (event.button !== 0) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    dragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      originX: offset.x,
      originY: offset.y,
    };
  }

  function handlePointerMove(event: ReactPointerEvent<HTMLDivElement>): void {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;

    setOffset({
      x: drag.originX + (event.clientX - drag.startX),
      y: drag.originY + (event.clientY - drag.startY),
    });
  }

  function handlePointerUp(event: ReactPointerEvent<HTMLDivElement>): void {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    dragRef.current = null;
    try {
      event.currentTarget.releasePointerCapture(event.pointerId);
    } catch {
      // ignore if capture already released
    }
  }

  return createPortal(
    <div className={`fixed inset-0 z-[100] ${overlayClassName}`}>
      <div className="absolute inset-0 bg-black/40" aria-hidden />
      <div className="relative flex h-full items-start justify-center overflow-y-auto px-4 py-0">
      <div
        className={`erp-card mt-0 flex max-h-full flex-col overflow-hidden bg-white shadow-xl ${className}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        style={{
          transform: `translate(${offset.x}px, ${offset.y}px)`,
          willChange: 'transform',
        }}
      >
        <div
          className="flex shrink-0 cursor-grab items-start justify-between gap-3 border-b border-secondary-200 px-5 py-4 active:cursor-grabbing select-none"
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
        >
          <div className="min-w-0">
            <h2 id={titleId} className="erp-card-title">
              {title}
            </h2>
            {subtitle ? <p className="erp-card-subtitle">{subtitle}</p> : null}
            <p className="mt-1 text-xs text-secondary-400">Arraste para reposicionar</p>
          </div>
          <button
            type="button"
            className="erp-btn-secondary shrink-0 px-2 py-1 text-sm"
            onClick={onClose}
            disabled={closeDisabled}
            aria-label="Fechar"
            onPointerDown={(event) => event.stopPropagation()}
          >
            ✕
          </button>
        </div>
        <div className={bodyClassName}>{children}</div>
        {footer ? (
          <div className="shrink-0 border-t border-secondary-200 bg-white px-4 py-4 sm:px-6">
            {footer}
          </div>
        ) : null}
      </div>
      </div>
    </div>,
    document.body,
  );
}
